import { useState, useCallback, useRef } from "react";
import $api from "../axios";

export interface IDaDataSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    postal_code: string | null;
    country: string;
    country_iso_code: string;
    federal_district: string;
    region_fias_id: string;
    region_kladr_id: string;
    region_iso_code: string;
    region_with_type: string;
    region_type: string;
    region_type_full: string;
    region: string;
    area_fias_id: string | null;
    area_kladr_id: string | null;
    area_with_type: string | null;
    area_type: string | null;
    area_type_full: string | null;
    area: string | null;
    city_fias_id: string;
    city_kladr_id: string;
    city_with_type: string;
    city_type: string;
    city_type_full: string;
    city: string;
    city_area: string | null;
    city_district_fias_id: string | null;
    city_district_kladr_id: string | null;
    city_district_with_type: string | null;
    city_district_type: string | null;
    city_district_type_full: string | null;
    city_district: string | null;
    settlement_fias_id: string | null;
    settlement_kladr_id: string | null;
    settlement_with_type: string | null;
    settlement_type: string | null;
    settlement_type_full: string | null;
    settlement: string | null;
    street_fias_id: string;
    street_kladr_id: string;
    street_with_type: string;
    street_type: string;
    street_type_full: string;
    street: string;
    stead_fias_id: string | null;
    stead_cadnum: string | null;
    stead_type: string | null;
    stead_type_full: string | null;
    stead: string | null;
    house_fias_id: string | null;
    house_kladr_id: string | null;
    house_cadnum: string | null;
    house_type: string | null;
    house_type_full: string | null;
    house: string | null;
    block_type: string | null;
    block_type_full: string | null;
    block: string | null;
    entrance: string | null;
    floor: string | null;
    flat_fias_id: string | null;
    flat_cadnum: string | null;
    flat_type: string | null;
    flat_type_full: string | null;
    flat: string | null;
    postal_box: string | null;
    fias_id: string;
    fias_code: string | null;
    fias_level: string;
    fias_actuality_state: string;
    kladr_id: string;
    geoname_id: string;
    capital_marker: string;
    okato: string;
    oktmo: string;
    tax_office: string;
    tax_office_legal: string;
    timezone: string | null;
    geo_lat: string;
    geo_lon: string;
    beltway_hit: string | null;
    beltway_distance: string | null;
    metro: string | null;
    divisions: any;
    qc_geo: string;
    qc_complete: string | null;
    qc_house: string | null;
    history_values: string[] | null;
    unparsed_parts: string | null;
    source: string | null;
    qc: string | null;
  };
}

export interface IDaDataResponse {
  data: IDaDataSuggestion[];
}

const useDaDataAddress = () => {
  const [suggestions, setSuggestions] = useState<IDaDataSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const fetchAddressSuggestions = useCallback(
    debounce(async (query: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (query.length < 2) {
        setSuggestions([]);
        setError("");
        return;
      }

      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError("");

      try {
        const response = await $api.get<IDaDataSuggestion[]>(
          "/integrations/dadata/nn-address-list",
          {
            params: { address: query },
            signal: abortControllerRef.current.signal,
            headers: { Accept: "application/json" },
          }
        );

        setSuggestions(response.data || []);
      } catch (err: any) {
        if (err.name === "AbortError") return;

        console.error("Ошибка при получении подсказок адресов:", err);
        setError("Не удалось загрузить подсказки адресов");
        setSuggestions([]);
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }, 300),
    []
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError("");
    setIsLoading(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    fetchAddressSuggestions,
    clearSuggestions,
  };
};

export default useDaDataAddress;
